import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { motion } from "framer-motion";
import PageHeader from "../components/PageHeader";
import { Reveal } from "../components/Reveal";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import { PillButton } from "../components/PillButton";
import { Mail, Phone, MapPin, Send, CheckCircle2, Clock, ArrowUpRight } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CONTACT_INFO = [
  { icon: Mail, label: "Email us", value: "hello@grooh.app", href: "mailto:hello@grooh.app" },
  { icon: Phone, label: "Call us", value: "+91 80 4718 2200", href: "tel:+918047182200" },
  { icon: MapPin, label: "Visit us", value: "Indiranagar, Bengaluru, IN", href: "#" },
  { icon: Clock, label: "Support hours", value: "Mon–Sat · 9am to 8pm", href: "#" },
];

const inputCls =
  "border-2 border-black rounded-xl h-12 px-4 font-medium text-base focus-visible:ring-4 focus-visible:ring-[#0033A2]/40 focus-visible:border-[#0033A2] focus-visible:ring-offset-0";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", society: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setDone(true);
      toast.success("Message sent! Our team will reach out within 24 hours.");
      setForm({ name: "", email: "", society: "", phone: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="contact-page">
      <PageHeader
        overline="Contact"
        title="Let's get your"
        highlight="society on grooh."
        subtitle="Book a free demo or drop us a message. Our team typically replies within 24 hours."
        testid="contact-header"
      />

      <section className="pb-24 md:pb-32">
        <div className="container-x grid md:grid-cols-12 gap-8">
          {/* Form */}
          <Reveal className="md:col-span-7">
            <div className="bg-white border-2 border-black rounded-3xl p-7 md:p-10 shadow-hard-sm">
              {done ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="text-center py-12"
                  data-testid="contact-success"
                >
                  <div className="w-16 h-16 rounded-full bg-[#0033A2] flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 size={32} className="text-white" strokeWidth={2.5} />
                  </div>
                  <h3 className="font-heading font-extrabold text-3xl tracking-tight">Message received!</h3>
                  <p className="mt-3 text-black/60 font-medium max-w-md mx-auto">
                    Thanks for reaching out. A member of the grooh team will be in touch very soon.
                  </p>
                  <PillButton
                    onClick={() => setDone(false)}
                    variant="ghost"
                    testid="contact-send-another"
                    className="mt-8"
                  >
                    Send another message
                  </PillButton>
                </motion.div>
              ) : (
                <form onSubmit={submit} data-testid="contact-form" className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name" className="font-heading font-bold">Full name *</Label>
                      <Input id="name" data-testid="contact-input-name" className={inputCls}
                        placeholder="Priya Sharma" value={form.name} onChange={update("name")} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email" className="font-heading font-bold">Email *</Label>
                      <Input id="email" type="email" data-testid="contact-input-email" className={inputCls}
                        placeholder="you@email.com" value={form.email} onChange={update("email")} />
                    </div>
                  </div>
                  <div className="grid md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="society" className="font-heading font-bold">Society name</Label>
                      <Input id="society" data-testid="contact-input-society" className={inputCls}
                        placeholder="Palm Meadows" value={form.society} onChange={update("society")} />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone" className="font-heading font-bold">Phone</Label>
                      <Input id="phone" data-testid="contact-input-phone" className={inputCls}
                        placeholder="+91 98765 43210" value={form.phone} onChange={update("phone")} />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="message" className="font-heading font-bold">Message *</Label>
                    <Textarea id="message" data-testid="contact-input-message" rows={5}
                      className="border-2 border-black rounded-xl px-4 py-3 font-medium text-base focus-visible:ring-4 focus-visible:ring-[#0033A2]/40 focus-visible:border-[#0033A2] focus-visible:ring-offset-0"
                      placeholder="Tell us about your society and what you're looking for..."
                      value={form.message} onChange={update("message")} />
                  </div>
                  <PillButton type="submit" testid="contact-submit" disabled={loading} className="w-full md:w-auto">
                    {loading ? "Sending..." : <>Send message <Send size={18} strokeWidth={2.5} /></>}
                  </PillButton>
                </form>
              )}
            </div>
          </Reveal>

          {/* Info */}
          <Reveal delay={0.1} className="md:col-span-5">
            <div className="bg-[#0033A2] text-white rounded-3xl p-8 md:p-10 border-2 border-black h-full">
              <h3 className="font-heading font-extrabold text-2xl tracking-tight mb-2">
                Prefer to reach out directly?
              </h3>
              <p className="text-white/70 font-medium mb-8">
                We'd love to hear from you. Here's how to find us.
              </p>
              <div className="space-y-5">
                {CONTACT_INFO.map((c) => {
                  const Icon = c.icon;
                  return (
                    <a
                      key={c.label}
                      href={c.href}
                      className="flex items-center gap-4 group"
                      data-testid={`contact-info-${c.label.toLowerCase().replace(/\s/g, "-")}`}
                    >
                      <div className="w-12 h-12 rounded-2xl bg-white/10 border-2 border-white/30 flex items-center justify-center shrink-0 group-hover:bg-white group-hover:text-[#0033A2] transition-colors">
                        <Icon size={20} strokeWidth={2.5} />
                      </div>
                      <div>
                        <p className="text-white/60 font-semibold text-xs uppercase tracking-widest">{c.label}</p>
                        <p className="font-heading font-bold text-lg">{c.value}</p>
                      </div>
                    </a>
                  );
                })}
              </div>

              <div className="mt-10 pt-8 border-t border-white/20">
                <p className="font-heading font-bold text-lg mb-3">Book a live demo</p>
                <p className="text-white/70 font-medium text-sm mb-5">
                  See grooh in action with a 20-minute walkthrough tailored to your society.
                </p>
                <PillButton href="mailto:hello@grooh.app" variant="white" testid="contact-book-demo">
                  Schedule now <ArrowUpRight size={18} strokeWidth={2.5} />
                </PillButton>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
